// PHA-1225: Phanova's default wording for the Program Confirmation Form (consent by text),
// for the Clinic Settings editor and its preview. The text that is actually sent is filled on
// the server (backend/app_server/app_logic.py: CONSENT_FORM_TITLE, DEFAULT_CONSENT_SMS_TEMPLATE,
// DEFAULT_CONSENT_FORM_TEXT, CONSENT_QUESTION, fill_consent_text, _consent_wording); keep
// these copies identical to those.

export const CONSENT_FORM_TITLE = 'Program Confirmation Form';

export const CONSENT_QUESTION = 'Do you agree to participate in the program?';

// Zack (2026-10-28): the defaults are the Phanova clinic's wording on dev.
export const DEFAULT_CONSENT_SMS_TEMPLATE =
  'Dear {first_name}, this is {clinic_name}\n' +
  'Please confirm whether you agree to join our care management program:\n' +
  '{link}\n' +
  'Reply STOP to opt out of texts.';

export const DEFAULT_CONSENT_FORM_TEXT = 'The Care Management Program includes weekly or monthly text surveys and follow-up calls.';

export function fillConsentText(
  template: string,
  values: { firstName?: string | null; clinicName?: string | null; link?: string },
): string {
  let text = template
    .split('{first_name}').join((values.firstName ?? '').trim() || 'there')
    .split('{clinic_name}').join((values.clinicName ?? '').trim() || 'your clinic');
  if (values.link !== undefined) text = text.split('{link}').join(values.link);
  return text.trim();
}

/** A clinic's wording for one language, as stored (null = not customised). */
export interface ConsentWording {
  consent_sms_template?: string | null;
  consent_form_text?: string | null;
  consent_question?: string | null;
}

const filled = (v?: string | null) => (v ?? '').trim() !== '';

/** Whether the clinic has written anything of its own for this language. A language without
 * it is sent with the clinic's English wording, and the patient's page is then English too. */
export function hasOwnConsentWording(w?: ConsentWording | null): boolean {
  return !!w && (filled(w.consent_sms_template) || filled(w.consent_form_text) || filled(w.consent_question));
}

/** The wording a language actually sends, field by field: its own text, else the clinic's
 * English, else Phanova's default. Same order as the server's _consent_wording. */
export function effectiveConsentWording(
  own: ConsentWording | null | undefined,
  english: ConsentWording | null | undefined,
): { smsTemplate: string; formText: string; question: string } {
  const englishSms = filled(english?.consent_sms_template) ? english!.consent_sms_template!.trim() : DEFAULT_CONSENT_SMS_TEMPLATE;
  const englishForm = filled(english?.consent_form_text) ? english!.consent_form_text!.trim() : DEFAULT_CONSENT_FORM_TEXT;
  const englishQuestion = filled(english?.consent_question) ? english!.consent_question!.trim() : CONSENT_QUESTION;
  if (!hasOwnConsentWording(own)) {
    return { smsTemplate: englishSms, formText: englishForm, question: englishQuestion };
  }
  const ownSms = (own?.consent_sms_template ?? '').trim();
  return {
    smsTemplate: ownSms && ownSms.includes('{link}') ? ownSms : englishSms,
    formText: filled(own?.consent_form_text) ? own!.consent_form_text!.trim() : englishForm,
    question: filled(own?.consent_question) ? own!.consent_question!.trim() : englishQuestion,
  };
}
